import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { mdiArrowRight } from '@mdi/js';
import { useEffect } from 'react';
import { DateTime } from 'luxon';

import { Divider } from 'common/components/Divider';
import { useOfferEventForCertificate } from 'offer/hooks';
import { IOfferEventType } from 'graphql/types';
import { OfferEventBasicListItem } from 'offer/components/OfferEventBasicListItem';
import { Button } from 'common/components/Button';
import { Icon } from 'common/components/Icon';
import { Link } from 'common/components/Link';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { IGlobalDrawerType } from 'common/types';

const OFFER_EVENT_INFO = {
    [IOfferEventType.Training]: {
        titleTranslationKey: 'receivedCertificateDrawer.trainings',
        types: [IOfferEventType.Training],
        queryParamType: 'training',
    },
    [IOfferEventType.Meeting]: {
        titleTranslationKey: 'receivedCertificateDrawer.meetings',
        types: [IOfferEventType.Meeting, IOfferEventType.Webinar],
        queryParamType: 'meeting-webinar',
    },
    [IOfferEventType.Course]: {
        titleTranslationKey: 'receivedCertificateDrawer.courses',
        types: [IOfferEventType.Course, IOfferEventType.Elearning],
        queryParamType: 'elearning',
    },
};

interface IProps {
    certificateId: string;
    type:
        | IOfferEventType.Training
        | IOfferEventType.Meeting
        | IOfferEventType.Course;
    isCurrentUserCertificate?: boolean;
    onLoading?(loading: boolean): void;
}

export const ReceivedCertificateOfferEvents = ({
    certificateId,
    type,
    isCurrentUserCertificate,
    onLoading,
}: IProps) => {
    const offerEventInfo = OFFER_EVENT_INFO[type];

    const [translate] = useTranslation();
    const { openGlobalDrawer } = useGlobalDrawer();
    const {
        offerEvents,
        loading: offerEventsLoading,
        count,
    } = useOfferEventForCertificate({
        certificateId,
        types: offerEventInfo.types,
        fromDate:
            type === IOfferEventType.Meeting
                ? DateTime.now().startOf('day')
                : undefined,
        // Only limit the events to the ones the actor can enroll
        // participants in when viewing another user's certificate.
        canEnrollParticipants: isCurrentUserCertificate ? undefined : true,
    });

    useEffect(() => {
        onLoading?.(offerEventsLoading);
    }, [offerEventsLoading]);

    if (offerEventsLoading || !offerEvents.length) return null;

    const { titleTranslationKey, queryParamType } = offerEventInfo;

    const moreResultsCount = (count || 0) - offerEvents.length;

    return (
        <Box sx={{ mt: 6 }}>
            <Typography variant="h3">
                {translate(titleTranslationKey)}
            </Typography>

            <Divider sx={{ my: 2 }} />

            {offerEvents.map((offerEvent) => (
                <OfferEventBasicListItem
                    withDate
                    key={offerEvent.id}
                    offerEvent={offerEvent}
                    onClick={() => {
                        openGlobalDrawer({
                            type: IGlobalDrawerType.OfferEvent,
                            itemId: offerEvent.id,
                        });
                    }}
                />
            ))}

            {!!moreResultsCount && (
                <Button
                    component={Link}
                    loading={offerEventsLoading}
                    query={`?certificateId=${certificateId}&view=list${!!queryParamType ? `&type=${queryParamType}` : ''}`}
                    size="small"
                    startIcon={<Icon path={mdiArrowRight} size="1.9rem" />}
                    to="OFFER_LIST"
                    variant="outlined"
                >
                    {translate('receivedCertificateDrawer.moreResultsButton', {
                        count: moreResultsCount,
                    })}
                </Button>
            )}
        </Box>
    );
};
